import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { PenTool, Upload, Trash2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Card, CardHeader, CardContent, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { api } from '../services/api';

export default function QaSignatureSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [responsibleName, setResponsibleName] = useState('');
  const [saving, setSaving] = useState(false);
  const [previewKey, setPreviewKey] = useState(Date.now()); // per bust cache

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/qa-settings');
      setSettings(res.data);
      if (res.data.responsible_name) setResponsibleName(res.data.responsible_name);
    } catch (e) {
      toast.error('Errore caricamento impostazioni QA');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!file) {
      toast.error('Seleziona un file firma (PNG/JPG/WEBP)');
      return;
    }
    if (!responsibleName.trim()) {
      toast.error("Inserisci il nome del Responsabile Qualità");
      return;
    }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('responsible_name', responsibleName.trim());
      await api.post('/qa-settings/signature', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Firma salvata');
      setFile(null);
      setPreviewKey(Date.now());
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio firma');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Rimuovere la firma? I PDF successivi non la conterranno più.')) return;
    try {
      await api.delete('/qa-settings/signature');
      toast.success('Firma rimossa');
      setResponsibleName('');
      setPreviewKey(Date.now());
      await load();
    } catch (e) {
      toast.error('Errore rimozione firma');
    }
  };

  if (loading) return <div className="text-center py-10"><div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto" /></div>;

  const previewUrl = settings?.has_signature
    ? `${process.env.REACT_APP_BACKEND_URL}/api/qa-settings/signature/preview?t=${previewKey}`
    : null;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="qa-signature-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <PenTool className="w-6 h-6 text-slate-700" />
          Firma Digitale Responsabile Qualità
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Carica un&apos;immagine della firma (scansione o firma digitale). Verrà apposta automaticamente sui PDF
          delle NC Cliente Ricevute pronte da inviare al cliente.
        </p>
      </div>

      {/* Stato attuale */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            {settings?.has_signature ? (
              <>
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                Firma configurata
              </>
            ) : (
              <>
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Nessuna firma configurata
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {settings?.has_signature ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Responsabile</div>
                <p className="text-lg font-semibold text-slate-900" data-testid="current-responsible-name">{settings.responsible_name || '—'}</p>
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mt-4 mb-2">Ultima modifica</div>
                <p className="text-slate-700 font-mono text-sm">{settings.updated_at ? new Date(settings.updated_at).toLocaleString('it-IT') : '—'}</p>
                <Button variant="destructive" size="sm" onClick={handleDelete} className="mt-4" data-testid="delete-signature-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Rimuovi firma
                </Button>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Anteprima</div>
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 bg-white flex items-center justify-center min-h-[160px]">
                  {previewUrl && (
                    <img
                      key={previewKey}
                      src={previewUrl}
                      alt="Firma"
                      className="max-h-32 object-contain"
                      data-testid="signature-preview"
                    />
                  )}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-slate-500 italic">Nessuna firma caricata. I PDF verranno prodotti con lo spazio firma vuoto (linea tratteggiata).</p>
          )}
        </CardContent>
      </Card>

      {/* Upload / Update */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{settings?.has_signature ? 'Aggiorna Firma' : 'Carica Firma'}</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>Nome Responsabile Qualità *</Label>
            <Input
              value={responsibleName}
              onChange={(e) => setResponsibleName(e.target.value)}
              placeholder="es. Mario Rossi"
              data-testid="responsible-name-input"
            />
          </div>
          <div className="space-y-1">
            <Label>File firma (PNG / JPG / WEBP, max 5MB) *</Label>
            <input
              type="file"
              accept=".png,.jpg,.jpeg,.webp"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded file:border-0 file:bg-slate-900 file:text-white hover:file:bg-slate-800 file:cursor-pointer"
              data-testid="signature-file-input"
              disabled={saving}
            />
            {file && (
              <p className="text-xs text-slate-500 mt-1">{file.name} · {(file.size / 1024).toFixed(1)} KB</p>
            )}
          </div>
          <div className="md:col-span-2 flex items-center justify-between">
            <p className="text-xs text-slate-500">
              Suggerimento: usa un&apos;immagine PNG a sfondo trasparente per il miglior risultato sui PDF.
            </p>
            <Button onClick={handleSave} disabled={saving || !file} data-testid="save-signature-btn">
              <Upload className="w-4 h-4 mr-1" />
              {saving ? 'Salvo...' : (settings?.has_signature ? 'Aggiorna Firma' : 'Carica Firma')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info */}
      <Card>
        <CardHeader><CardTitle className="text-base">Dove viene usata?</CardTitle></CardHeader>
        <CardContent className="text-sm text-slate-700 space-y-2">
          <p>Attualmente la firma viene apposta automaticamente su:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li><strong>Report NC Cliente Ricevuta</strong> — sezione &quot;Firma&quot; a piè di pagina.</li>
            <li><strong>Fascicolo Completo NC Cliente</strong> — nella pagina Report che apre il fascicolo.</li>
          </ul>
          <p className="text-slate-500 text-xs mt-3">
            La rimozione della firma non modifica retroattivamente i PDF già generati e scaricati.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
